'use client';

import { useState, useEffect } from 'react';
import SolutionCard from './SolutionCard';
import ComparisonMatrix from './ComparisonMatrix';
import TechnicalSpecifications from './TechnicalSpecifications';
import IntegrationShowcase from './IntegrationShowcase';
import MetricsVisualization from './MetricsVisualization';
import IndustryFilter from './IndustryFilter';
import Icon from '@/components/ui/AppIcon';

interface Solution {
  id: string;
  title: string;
  category: 'Safety' | 'Security' | 'Accountability';
  description: string;
  image: string;
  imageAlt: string;
  features: {
    icon: string;
    title: string;
    description: string;
  }[];
  metrics: {
    value: string;
    label: string;
  }[];
  caseStudyPreview?: string;
  industries: string[];
}

const SolutionsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [filteredIndustries, setFilteredIndustries] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const solutions: Solution[] = [
  {
    id: 'ppe-detection',
    title: 'PPE Detection & Compliance',
    category: 'Safety',
    description: 'Real-time monitoring of personal protective equipment compliance with instant alerts for violations. Ensures worker safety through continuous AI-powered surveillance.',
    image: "https://images.unsplash.com/photo-1690947154962-b370d32e3f28",
    imageAlt: 'Construction worker wearing yellow hard hat and safety vest on industrial site',
    features: [
    {
      icon: 'ShieldCheckIcon',
      title: 'Multi-PPE Detection',
      description: 'Detects helmets, vests, gloves, goggles, and safety boots simultaneously'
    },
    {
      icon: 'BellAlertIcon',
      title: 'Instant Alerts',
      description: 'Real-time notifications to supervisors when violations occur'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Compliance Reports',
      description: 'Automated daily, weekly, and monthly compliance analytics'
    }],

    metrics: [
    { value: '87%', label: 'Incident Reduction' },
    { value: '99.2%', label: 'Detection Accuracy' },
    { value: '<2s', label: 'Alert Response' }],

    caseStudyPreview: 'Manufacturing plant reduced safety incidents by 87% within 6 months of implementation.',
    industries: ['manufacturing', 'construction', 'energy']
  },
  {
    id: 'behavioral-monitoring',
    title: 'Behavioral Safety Monitoring',
    category: 'Safety',
    description: 'Advanced AI analysis of worker behavior patterns to identify unsafe practices before incidents occur. Predictive safety intelligence for proactive risk mitigation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2d37b38-1764311934118.png",
    imageAlt: 'Industrial worker monitoring safety systems on laptop in control room',
    features: [
    {
      icon: 'EyeIcon',
      title: 'Pattern Recognition',
      description: 'Identifies unsafe behavior patterns and near-miss situations'
    },
    {
      icon: 'ExclamationTriangleIcon',
      title: 'Risk Scoring',
      description: 'AI-powered risk assessment for each detected behavior'
    },
    {
      icon: 'AcademicCapIcon',
      title: 'Training Insights',
      description: 'Data-driven recommendations for targeted safety training'
    }],

    metrics: [
    { value: '73%', label: 'Near-Miss Prevention' },
    { value: '94%', label: 'Behavior Accuracy' },
    { value: '45%', label: 'Training Efficiency' }],

    caseStudyPreview: 'Logistics facility prevented 73% of potential near-miss incidents through predictive monitoring.',
    industries: ['manufacturing', 'logistics', 'mining']
  },
  {
    id: 'gate-management',
    title: 'Intelligent Gate Management',
    category: 'Security',
    description: 'Automated vehicle and personnel access control with license plate recognition, facial verification, and real-time entry/exit logging for comprehensive facility security.',
    image: "https://images.unsplash.com/photo-1627487559207-d0755eadb2cf",
    imageAlt: 'Modern industrial facility entrance gate with security checkpoint and barrier',
    features: [
    {
      icon: 'IdentificationIcon',
      title: 'Multi-Modal Verification',
      description: 'License plate, facial recognition, and RFID badge integration'
    },
    {
      icon: 'ClockIcon',
      title: 'Automated Logging',
      description: 'Complete entry/exit records with timestamp and vehicle details'
    },
    {
      icon: 'LockClosedIcon',
      title: 'Access Control',
      description: 'Rule-based authorization with blacklist/whitelist management'
    }],

    metrics: [
    { value: '100%', label: 'Entry Logging' },
    { value: '98.7%', label: 'Recognition Rate' },
    { value: '65%', label: 'Time Savings' }],

    caseStudyPreview: 'Energy facility achieved 100% entry logging accuracy while reducing gate processing time by 65%.',
    industries: ['energy', 'manufacturing', 'logistics']
  },
  {
    id: 'intrusion-detection',
    title: 'Perimeter Intrusion Detection',
    category: 'Security',
    description: 'AI-powered perimeter monitoring with intelligent threat classification, reducing false alarms while ensuring rapid response to genuine security breaches.',
    image: "https://images.unsplash.com/photo-1687802826923-f1bef7d97234",
    imageAlt: 'Industrial facility perimeter fence with security cameras at dusk',
    features: [
    {
      icon: 'MapIcon',
      title: 'Zone-Based Monitoring',
      description: 'Customizable security zones with different alert priorities'
    },
    {
      icon: 'SparklesIcon',
      title: 'Smart Classification',
      description: 'Distinguishes between humans, vehicles, and animals'
    },
    {
      icon: 'BoltIcon',
      title: 'Instant Response',
      description: 'Automated alerts with video evidence to security teams'
    }],

    metrics: [
    { value: '92%', label: 'False Alarm Reduction' },
    { value: '3.2s', label: 'Detection Speed' },
    { value: '99.5%', label: 'Threat Accuracy' }],

    caseStudyPreview: 'Mining operation reduced false alarms by 92% while maintaining 99.5% threat detection accuracy.',
    industries: ['mining', 'energy', 'construction']
  },
  {
    id: 'aina-analytics',
    title: 'AINA Analytics Platform',
    category: 'Accountability',
    description: 'Comprehensive AI-powered analytics dashboard providing actionable insights from all monitoring systems. Transform raw data into strategic safety and security intelligence.',
    image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74",
    imageAlt: 'Modern analytics dashboard displaying colorful charts and graphs on large monitor',
    features: [
    {
      icon: 'PresentationChartLineIcon',
      title: 'Custom Dashboards',
      description: 'Role-based views for executives, managers, and operators'
    },
    {
      icon: 'DocumentChartBarIcon',
      title: 'Automated Reporting',
      description: 'Scheduled reports with compliance metrics and trend analysis'
    },
    {
      icon: 'CogIcon',
      title: 'Predictive Analytics',
      description: 'Machine learning models for incident prediction and prevention'
    }],

    metrics: [
    { value: '360°', label: 'Visibility' },
    { value: '85%', label: 'Decision Speed' },
    { value: '12hrs', label: 'Time Saved/Week' }],

    caseStudyPreview: 'Operations team improved decision-making speed by 85% with centralized analytics platform.',
    industries: ['manufacturing', 'logistics', 'energy', 'mining', 'construction']
  },
  {
    id: 'theft-prevention',
    title: 'Asset Theft Prevention',
    category: 'Security',
    description: 'AI-powered monitoring of high-value assets with movement tracking, unauthorized access detection, and real-time theft prevention alerts.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1448a57d5-1764311935007.png",
    imageAlt: 'Secure warehouse with organized inventory and surveillance cameras',
    features: [
    {
      icon: 'CubeIcon',
      title: 'Asset Tracking',
      description: 'Monitor movement and location of valuable equipment and materials'
    },
    {
      icon: 'ShieldExclamationIcon',
      title: 'Anomaly Detection',
      description: 'Identify unusual patterns indicating potential theft attempts'
    },
    {
      icon: 'VideoCameraIcon',
      title: 'Evidence Capture',
      description: 'Automatic video recording and archival of suspicious activities'
    }],

    metrics: [
    { value: '78%', label: 'Theft Reduction' },
    { value: '96%', label: 'Detection Rate' },
    { value: '$2.4M', label: 'Annual Savings' }],

    caseStudyPreview: 'Construction site reduced equipment theft by 78%, saving $2.4M annually in replacement costs.',
    industries: ['construction', 'logistics', 'manufacturing']
  }];


  const industries = [
  { id: 'manufacturing', name: 'Manufacturing', icon: 'CogIcon' },
  { id: 'logistics', name: 'Logistics', icon: 'TruckIcon' },
  { id: 'energy', name: 'Energy', icon: 'BoltIcon' },
  { id: 'mining', name: 'Mining', icon: 'CubeIcon' },
  { id: 'construction', name: 'Construction', icon: 'WrenchScrewdriverIcon' }];


  const comparisonFeatures = [
  { name: 'Real-time Monitoring', safety: true, security: true, accountability: true },
  { name: 'PPE Detection', safety: true, security: false, accountability: false },
  { name: 'Behavioral Analysis', safety: true, security: false, accountability: true },
  { name: 'Access Control', safety: false, security: true, accountability: true },
  { name: 'Perimeter Protection', safety: false, security: true, accountability: false },
  { name: 'Theft Prevention', safety: false, security: true, accountability: true },
  { name: 'Custom Dashboards', safety: true, security: true, accountability: true },
  { name: 'Predictive Analytics', safety: true, security: true, accountability: true },
  { name: 'Compliance Reporting', safety: true, security: false, accountability: true },
  { name: 'Incident Documentation', safety: true, security: true, accountability: true }];


  const technicalSpecs = [
  {
    category: 'AI Vision Capabilities',
    specs: [
    {
      name: 'Detection Accuracy',
      value: '99.2%',
      description: 'Industry-leading accuracy across all detection models with continuous learning'
    },
    {
      name: 'Processing Speed',
      value: '30 FPS',
      description: 'Real-time video analysis at 30 frames per second per camera stream'
    },
    {
      name: 'Simultaneous Detections',
      value: '50+ Objects',
      description: 'Track multiple objects, people, and events simultaneously in single frame'
    }]

  },
  {
    category: 'Edge Computing Infrastructure',
    specs: [
    {
      name: 'On-Premise Processing',
      value: '100%',
      description: 'All AI processing happens locally for data privacy and low latency'
    },
    {
      name: 'Camera Support',
      value: '64 Streams',
      description: 'Single edge device supports up to 64 concurrent camera streams'
    },
    {
      name: 'Failover Protection',
      value: 'Redundant',
      description: 'Automatic failover to backup systems ensures continuous monitoring'
    }]

  },
  {
    category: 'Integration & Connectivity',
    specs: [
    {
      name: 'API Access',
      value: 'RESTful',
      description: 'Complete REST API for integration with existing enterprise systems'
    },
    {
      name: 'Network Requirements',
      value: 'LTE/5G',
      description: 'Works with standard LTE, 5G, or wired network infrastructure'
    },
    {
      name: 'Data Export',
      value: 'Multiple Formats',
      description: 'Export data in CSV, JSON, PDF, and Excel formats for analysis'
    }]

  }];


  const integrations = [
  {
    name: 'SAP ERP',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa059bc2-1764311931684.png",
    logoAlt: 'SAP enterprise resource planning software logo',
    category: 'Enterprise Resource Planning',
    description: 'Seamless integration with SAP for workforce management and incident reporting'
  },
  {
    name: 'Zoho',
    logo: "assets/images/zohoone-logo.png",
    logoAlt: 'Zoho business software suite logo',
    category: 'Business Software',
    description: 'Integrate with Zoho for automated reporting and workflow management'
  },
  {
    name: 'Google Sheets',
    logo: "assets/images/google-sheet.png",
    logoAlt: 'Google Sheets spreadsheet logo',
    category: 'Productivity',
    description: 'Export analytics and reports directly to Google Sheets for collaboration'
  },
  // {
  //   name: 'Microsoft Azure',
  //   logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12f43fe46-1764311932356.png",
  //   logoAlt: 'Microsoft Azure cloud platform logo',
  //   category: 'Cloud Infrastructure',
  //   description: 'Cloud storage and backup integration for long-term data retention'
  // },
  // {
  //   name: 'Honeywell Security',
  //   logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13e9f97b4-1764311931903.png",
  //   logoAlt: 'Honeywell security systems logo',
  //   category: 'Security Systems',
  //   description: 'Direct integration with Honeywell access control and alarm systems'
  // },
  // {
  //   name: 'Siemens SCADA',
  //   logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1636667da-1764311931703.png",
  //   logoAlt: 'Siemens industrial automation logo',
  //   category: 'Industrial Automation',
  //   description: 'Connect with SCADA systems for operational technology integration'
  // },
  // {
  //   name: 'Genetec Security Center',
  //   logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1427b908e-1764311931507.png",
  //   logoAlt: 'Genetec security management platform logo',
  //   category: 'Video Management',
  //   description: 'Native integration with Genetec VMS for unified security operations'
  // },
  // {
  //   name: 'Workday HCM',
  //   logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c29b16be-1764311932000.png",
  //   logoAlt: 'Workday human capital management logo',
  //   category: 'Human Resources',
  //   description: 'Sync employee data for access control and safety training tracking'
  // }
];


  const metricsData = [
  { category: 'Safety Incidents', before: 45, after: 6 },
  { category: 'Security Breaches', before: 28, after: 3 },
  { category: 'Compliance Violations', before: 67, after: 9 },
  { category: 'Response Time (min)', before: 18, after: 2 }];


  const filteredSolutions = solutions.filter((solution) => {
    const categoryMatch = selectedCategory === 'all' || solution.category === selectedCategory;
    const industryMatch = filteredIndustries.length === 0 ||
    filteredIndustries.some((industry) => solution.industries.includes(industry));
    return categoryMatch && industryMatch;
  });

  const handleIndustryFilterChange = (selectedIndustries: string[]) => {
    setFilteredIndustries(selectedIndustries);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:400:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="400" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:401:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="401" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20px-4%20lg%3Apx-8%20py-16%22%7D" className="w-full px-4 lg:px-8 py-16">
          <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:402:10" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="402" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:403:12" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="403" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:404:12" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="404" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20w-2%2F3%22%7D" className="h-6 bg-muted rounded w-2/3"></div>
            <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:405:12" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="405" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:407:14" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="407" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:417:4" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="417" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20space-y-16%22%7D" className="w-full space-y-16">
      {/* Category Filter */}
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:419:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="419" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-3%20justify-center%22%7D" className="flex flex-wrap gap-3 justify-center">
        {['all', 'Safety', 'Security', 'Accountability'].map((category) =>
        <button data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:421:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="421" data-component-file="SolutionsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          key={category}
          onClick={() => setSelectedCategory(category)}
          className={`px-6 py-3 rounded-lg font-semibold transition-smooth ${
          selectedCategory === category ?
          'bg-primary text-white shadow-brand' :
          'bg-card text-text-secondary border border-border hover:border-primary hover:text-primary'}`
          }>

            {category === 'all' ? 'All Solutions' : category}
          </button>
        )}
      </div>

      {/* Industry Filter */}
      <IndustryFilter data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:436:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="436" data-component-file="SolutionsInteractive.tsx" data-component-name="IndustryFilter" data-component-content="%7B%22elementName%22%3A%22IndustryFilter%22%7D" industries={industries} onFilterChange={handleIndustryFilterChange} />

      {/* Solutions Grid */}
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:439:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="439" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
        {filteredSolutions.map((solution) =>
        <SolutionCard data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:441:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="441" data-component-file="SolutionsInteractive.tsx" data-component-name="SolutionCard" data-component-content="%7B%22elementName%22%3A%22SolutionCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={solution.id} {...solution} />
        )}
      </div>

      {filteredSolutions.length === 0 &&
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:446:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="446" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
          <Icon data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:447:10" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="447" data-component-file="SolutionsInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%2C%22className%22%3A%22text-text-tertiary%20mx-auto%20mb-4%22%7D" name="FunnelIcon" size={48} className="text-text-tertiary mx-auto mb-4" />
          <p data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:448:10" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="448" data-component-file="SolutionsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%22%2C%22textContent%22%3A%22No%20solutions%20match%20your%20selected%20filters%22%7D" className="text-lg text-text-secondary">No solutions match your selected filters</p>
          <button data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:449:10" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="449" data-component-file="SolutionsInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22mt-4%20px-6%20py-2%20bg-primary%20text-white%20rounded-lg%20hover%3Abg-primary%2F90%20transition-smooth%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D"
          onClick={() => {
            setSelectedCategory('all');
            setFilteredIndustries([]);
          }}
          className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth">

            Clear All Filters
          </button>
        </div>
      }

      {/* Metrics Visualization */}
      <MetricsVisualization data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:462:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="462" data-component-file="SolutionsInteractive.tsx" data-component-name="MetricsVisualization" data-component-content="%7B%22elementName%22%3A%22MetricsVisualization%22%7D"
        title="Before & After Implementation Impact"
        data={metricsData}
        yAxisLabel="Count / Minutes" />


      {/* Comparison Matrix */}
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:469:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="469" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <h2 data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:470:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="470" data-component-file="SolutionsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20mb-6%22%2C%22textContent%22%3A%22Solution%20Comparison%20Matrix%22%7D" className="text-3xl font-bold text-text-primary mb-6">Solution Comparison Matrix</h2>
        <ComparisonMatrix data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:471:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="471" data-component-file="SolutionsInteractive.tsx" data-component-name="ComparisonMatrix" data-component-content="%7B%22elementName%22%3A%22ComparisonMatrix%22%7D" features={comparisonFeatures} />
      </div>

      {/* Technical Specifications */}
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:475:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="475" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <h2 data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:476:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="476" data-component-file="SolutionsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20mb-6%22%2C%22textContent%22%3A%22Technical%20Specifications%22%7D" className="text-3xl font-bold text-text-primary mb-6">Technical Specifications</h2>
        <TechnicalSpecifications data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:477:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="477" data-component-file="SolutionsInteractive.tsx" data-component-name="TechnicalSpecifications" data-component-content="%7B%22elementName%22%3A%22TechnicalSpecifications%22%7D" specifications={technicalSpecs} />
      </div>

      {/* Integration Showcase */}
      <div data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:481:6" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="481" data-component-file="SolutionsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <h2 data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:482:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="482" data-component-file="SolutionsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20mb-6%22%2C%22textContent%22%3A%22Enterprise%20Integrations%22%7D" className="text-3xl font-bold text-text-primary mb-6">Enterprise Integrations</h2>
        <IntegrationShowcase data-component-id="src/app/solutions-overview/components/SolutionsInteractive.tsx:483:8" data-component-path="src/app/solutions-overview/components/SolutionsInteractive.tsx" data-component-line="483" data-component-file="SolutionsInteractive.tsx" data-component-name="IntegrationShowcase" data-component-content="%7B%22elementName%22%3A%22IntegrationShowcase%22%7D" integrations={integrations} />
      </div>
    </div>);

};

export default SolutionsInteractive;